import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { supabase } from "@/integrations/supabase/client";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { toast } from "@/hooks/use-toast";
import { Label } from "@/components/ui/label";
import { AuthLogo } from "@/components/auth/AuthLogo";
import { ArrowLeft, Check, Loader2 } from "lucide-react";

const ResetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isValidSession, setIsValidSession] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);

  const formSchema = z.object({
    password: z.string().min(8, { message: "La password deve avere almeno 8 caratteri" }),
    confirmPassword: z.string(),
  }).refine((data) => data.password === data.confirmPassword, {
    message: "Le password non corrispondono",
    path: ["confirmPassword"],
  });

  type FormData = z.infer<typeof formSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });

  // Check if user has a valid recovery session
  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        // If user came from recovery link, they'll have a session
        if (session) {
          setIsValidSession(true);
        } else {
          // Try to get session from URL hash (Supabase recovery flow)
          const hashParams = new URLSearchParams(window.location.hash.substring(1));
          const accessToken = hashParams.get('access_token');
          const refreshToken = hashParams.get('refresh_token');
          
          if (accessToken && refreshToken) {
            const { error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });
            
            if (!error) {
              setIsValidSession(true);
            }
          }
        }
      } catch (error) {
        console.error("Error checking session:", error);
      } finally {
        setCheckingSession(false);
      }
    };

    checkSession();
  }, []);

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    
    try {
      const { error } = await supabase.auth.updateUser({
        password: data.password,
      });

      if (error) {
        toast({ title: error.message || "Errore durante l'aggiornamento della password", variant: "error" });
      } else {
        toast({ title: "Password aggiornata con successo!", variant: "success" });
        setIsSuccess(true);
        
        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate("/auth/login");
        }, 2000);
      }
    } catch (error: any) {
      toast({ title: error.message || "Errore durante l'aggiornamento della password", variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  // Loading state
  if (checkingSession) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center p-4">
        <BrutalCard className="w-full max-w-md text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p className="font-body text-muted-foreground">Verifica sessione...</p>
        </BrutalCard>
      </div>
    );
  }

  // Invalid session
  if (!isValidSession) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center p-4">
        <BrutalCard className="w-full max-w-md">
          <div className="text-center mb-8">
            <AuthLogo />
            <h1 className="font-heading font-bold text-3xl text-dark mb-2">
              Link non valido
            </h1>
            <p className="text-muted-foreground font-body">
              Il link di reset password è scaduto o non è valido. Richiedi un nuovo link.
            </p>
          </div>

          <div className="space-y-4">
            <Link to="/auth/forgot-password">
              <BrutalButton type="button" className="w-full">
                Richiedi nuovo link
              </BrutalButton>
            </Link>
            
            <Link to="/auth/login">
              <BrutalButton type="button" variant="outline" className="w-full">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Torna al login
              </BrutalButton>
            </Link>
          </div>
        </BrutalCard>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center p-4">
      <BrutalCard className="w-full max-w-md">
        <div className="text-center mb-8">
          <AuthLogo />
          <h1 className="font-heading font-bold text-3xl text-dark mb-2">
            Nuova Password
          </h1>
          <p className="text-muted-foreground font-body">
            {isSuccess ? "Password aggiornata!" : "Inserisci la tua nuova password"}
          </p>
        </div>

        {!isSuccess ? (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <Label htmlFor="password" className="font-heading font-bold uppercase text-xs mb-2 block">
                Nuova Password
              </Label>
              <BrutalInput
                id="password"
                type="password"
                {...register("password")}
                disabled={isLoading}
              />
              {errors.password && (
                <p className="text-destructive text-sm mt-1">{errors.password.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="font-heading font-bold uppercase text-xs mb-2 block">
                Conferma Password
              </Label>
              <BrutalInput
                id="confirmPassword"
                type="password"
                {...register("confirmPassword")}
                disabled={isLoading}
              />
              {errors.confirmPassword && (
                <p className="text-destructive text-sm mt-1">{errors.confirmPassword.message}</p>
              )}
            </div>

            <BrutalButton
              type="submit"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Aggiornamento...
                </>
              ) : (
                "Aggiorna Password"
              )}
            </BrutalButton>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="bg-success/20 border-[3px] border-success p-4 flex items-center gap-3">
              <Check className="w-6 h-6 text-success" />
              <p className="font-body text-sm text-dark">
                Password aggiornata con successo! Reindirizzamento al login...
              </p>
            </div>
          </div>
        )}
      </BrutalCard>
    </div>
  );
};

export default ResetPassword;